import {
  MimecastAwsRegions,
  MimecastEnvs,
  createAwsRumClient,
} from "./createAwsRumClient"

export function getMimecastCgEnv(): {
  region: MimecastAwsRegions
  env: MimecastEnvs
} {
  const { hostname = "" } = window.location

  if (hostname === "localhost") {
    return { region: "eu-west-2", env: "LOCAL" }
  }

  if (hostname.endsWith("mimecast-dev.com")) {
    return { region: "eu-west-2", env: "DEV" }
  }

  if (hostname.endsWith("mimecast-qa.com")) {
    return { region: "eu-west-2", env: "STG" }
  }

  const prodEnvs: { [key: string]: ReturnType<typeof getMimecastCgEnv> } = {
    "login-au.mimecast.com": { region: "ap-southeast-2", env: "PROD" },
    "login-ca.mimecast.com": { region: "ca-central-1", env: "PROD" },
    "login-de.mimecast.com": { region: "eu-central-1", env: "PROD" },
    "login-uk.mimecast.com": { region: "eu-west-2", env: "PROD" },
    "login-us.mimecast.com": { region: "us-east-1", env: "PROD" },
    "login-usb.mimecast.com": { region: "us-west-2", env: "PROD" },
    "login-za.mimecast.com": { region: "af-south-1", env: "PROD" },
  }

  return prodEnvs[hostname] ?? { region: "UNKNOWN", env: "UNKNOWN" }
}

export function createMimecastCgAwsRum() {
  try {
    const { env, region } = getMimecastCgEnv()

    const [subdomain] = window.location.hostname.split(".")

    const { appId, identityPoolId } = getRumConfig(env, region)

    return createAwsRumClient({
      appId,
      identityPoolId,
      mimecastAwsRegion: region,
      mimecastEnv: env,
      attrs: { SUBDOMAIN: subdomain },
      baseUrl: "/",
    })
  } catch (e) {
    // Ignore errors during initialization so RUM doesn't block the app from actually working
    console.error("Failed to init RUM:", e)
    return null
  }
}

function getRumConfig(
  env: MimecastEnvs,
  region: MimecastAwsRegions,
): { appId: string; identityPoolId: string } {
  switch (env) {
    case "PROD":
      return {
        "ap-southeast-2": {
          appId: "20ffcf5e-c10b-4e67-92b2-c6e92d96d8d1",
          identityPoolId: "ap-southeast-2:0cb35386-f0d5-4112-bb0b-e8d76eebf095",
        },
        "ca-central-1": {
          appId: "c4ac8d9d-b33a-457e-92d7-5f416444355a",
          identityPoolId: "ca-central-1:e49df3fa-90d0-40f3-9df3-08346d431f17",
        },
        "eu-central-1": {
          appId: "a0721c77-90be-4245-89b4-e03553229563",
          identityPoolId: "eu-central-1:d45bbaec-3f87-48c0-833e-6bd9d363cb8e",
        },
        "eu-west-2": {
          appId: "09a0a5ec-de9b-47cc-8c85-05985c6d3b47",
          identityPoolId: "eu-west-2:96df4440-0ccf-42a5-966c-6d669e204ce8",
        },
        "us-east-1": {
          appId: "7f36bdc1-814b-40cf-997f-1c28a325ef8d",
          identityPoolId: "us-east-1:47d34439-719b-494e-be13-52dcb531b7cb",
        },
        "us-west-2": {
          appId: "12caa935-d155-4d82-9d48-e06a2995ec4a",
          identityPoolId: "us-west-2:83cd5509-bfe1-4237-a613-358fdd320ee9",
        },
        "af-south-1": {
          appId: "b07ad491-5748-4893-8858-7dbb968c7df2",
          identityPoolId: "af-south-1:662a198c-e0fc-467f-af5d-64f9aabebbbb",
        },
        UNKNOWN: { appId: "", identityPoolId: "" },
      }[region]
    case "STG":
      return {
        appId: "9f4e51b6-e646-410e-813b-9ab38e06c1fb",
        identityPoolId: "eu-west-2:ddeb31fb-c887-422d-a382-55598f487d49",
      }
    case "DEV":
      return {
        appId: "0ff50738-bf8b-4eaa-8b7a-6f2aeef7a390",
        identityPoolId: "eu-west-2:90e2f862-bfc5-4acc-9e8f-8018e2fc1517",
      }
    case "LOCAL":
      return {
        appId: "0d3dcaa4-0d49-4363-8eb6-49f4871f2be0",
        identityPoolId: "eu-west-2:7e66f95a-7698-4a94-92fe-1a28b1f8df3e",
      }
    case "UNKNOWN":
    default:
      return { appId: "", identityPoolId: "" }
  }
}
